import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  History, 
  Users, 
  LogOut,
  BarChart3,
  Menu,
  X,
  Settings,
  Building2,
  ExternalLink
} from 'lucide-react';
import { useState, useEffect } from 'react';

const DEFAULT_LOGO_URL = 'https://customer-assets.emergentagent.com/job_6b686586-6fa7-4e5a-99dc-cb02e8ae2891/artifacts/ub4cxec7_Logo%20004.png';
const COMPANY_CACHE_PREFIX = 'cod4x_company_cache_';

export const Sidebar = () => {
  const { user, logout, isAdmin, isGestor, api } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [company, setCompany] = useState(null);

  const getCompanyCacheKey = () => `${COMPANY_CACHE_PREFIX}${user?.company_id || 'none'}`;

  useEffect(() => {
    // Buscar dados da empresa do usuário
    if (user?.company_id && api) {
      let hasCache = false;
      try {
        const cached = localStorage.getItem(getCompanyCacheKey());
        if (cached) {
          setCompany(JSON.parse(cached));
          hasCache = true;
        }
      } catch (error) {
        // Ignore malformed cache and continue with fresh fetch.
      }

      // Evita recarregar a logo a cada troca de rota quando já há cache válido.
      if (!hasCache) {
        fetchCompanyData();
      }
    } else {
      setCompany(null);
    }
  }, [user?.company_id]);

  const fetchCompanyData = async () => {
    try {
      const response = await api.get(`/companies/${user.company_id}`);
      setCompany(response.data);
      localStorage.setItem(getCompanyCacheKey(), JSON.stringify(response.data));
    } catch (error) {
      console.error('Erro ao buscar empresa:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Logo e nome da empresa
  const logoUrl = company?.logo_url 
    ? (company.logo_url.startsWith('/api') 
        ? `${process.env.REACT_APP_BACKEND_URL}${company.logo_url}` 
        : company.logo_url)
    : DEFAULT_LOGO_URL;
  const companyName = company?.name || 'Conferência';

  const navItems = [
    { 
      to: '/dashboard', 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      show: true,
      section: 'main'
    },
    { 
      to: '/audit', 
      icon: History, 
      label: 'Auditoria',
      show: isGestor(),
      section: 'main'
    },
    { 
      to: '/reports', 
      icon: BarChart3, 
      label: 'Relatórios',
      show: isGestor(),
      section: 'main'
    },
    { 
      to: '/settings', 
      icon: Settings, 
      label: 'Configurações',
      show: true,
      section: 'main'
    },
    // Gestão de Usuários - todos podem acessar (filtrado por permissão no backend)
    { 
      to: '/users', 
      icon: Users, 
      label: 'Usuários',
      show: true,  // Todos veem, mas o conteúdo é filtrado pelo backend
      section: 'admin'
    },
    // Gestão de Empresas - apenas admin_master
    { 
      to: '/super-admin', 
      icon: Building2, 
      label: 'Empresas',
      show: isAdmin(),  // Apenas admin_master
      section: 'admin',
      external: true
    },
  ];

  const mainItems = navItems.filter(item => item.show && item.section === 'main');
  const adminItems = navItems.filter(item => item.show && item.section === 'admin');

  const SidebarContent = () => (
    <>
      {/* Logo e Nome da Empresa */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
            {company?.logo_url ? (
              <img 
                src={logoUrl} 
                alt={companyName}
                className="w-full h-full object-contain p-1"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.parentElement?.querySelector('[data-logo-fallback]');
                  if (fallback) fallback.style.display = 'flex';
                }}
                data-testid="sidebar-company-logo"
              />
            ) : null}
            <div 
              data-logo-fallback
              className={`items-center justify-center ${company?.logo_url ? 'hidden' : 'flex'}`}
              style={{display: company?.logo_url ? 'none' : 'flex'}}
            >
              <Building2 className="w-6 h-6 text-white/60" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h2 
              className="text-sm font-bold text-white truncate" 
              title={companyName}
              data-testid="sidebar-company-name"
            >
              {companyName}
            </h2>
            <p className="text-xs text-white/50">Conferência</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {/* Main Navigation */}
        {mainItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-colors ${
                isActive ? 'bg-white/10 text-white border-l-4 border-white' : ''
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon size={20} />
            <span className="font-medium">{item.label}</span>
          </NavLink>
        ))}

        {/* Admin Section - apenas para admin_master */}
        {adminItems.length > 0 && (
          <>
            <div className="px-6 py-3 mt-4 border-t border-white/10">
              <p className="text-xs font-semibold text-white/40 uppercase tracking-wider">
                Painel Administrativo
              </p>
            </div>
            {adminItems.map((item) => (
              item.external ? (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-6 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.label}</span>
                  <ExternalLink size={14} className="ml-auto text-white/40" />
                </a>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-colors ${
                      isActive ? 'bg-white/10 text-white border-l-4 border-white' : ''
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              )
            ))}
          </>
        )}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-white/10">
        <div className="mb-3 px-2">
          <p className="text-sm font-medium text-white truncate">{user?.name}</p>
          <p className="text-xs text-white/60 capitalize">{user?.role?.replace('_', ' ')}</p>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-4 py-2 text-white/80 hover:bg-white/10 hover:text-white rounded-lg transition-colors"
          data-testid="logout-button"
        >
          <LogOut size={20} />
          <span>Sair</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed top-4 left-4 z-50 p-2 bg-[#1a365d] text-white rounded-lg md:hidden"
        data-testid="mobile-menu-button"
      >
        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <aside 
        className={`fixed left-0 top-0 h-full w-64 bg-[#1a365d] text-white z-50 transform transition-transform duration-300 md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <SidebarContent />
        </div>
      </aside>

      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 bg-[#1a365d] text-white z-40 flex-col">
        <SidebarContent />
      </aside>
    </>
  );
};
